import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./discoverMapPage.css";
import generalRequest from "../../request/generalRequest";
import MapEvents from "../../utils/mapEvents";
import { Link, useNavigate } from "react-router-dom";
import DiscoverMapListSkeleton from "./discoverMapPageSkeleton/discoverMapPageSkeleton";

const formatRating = (rating) => {
	if (rating == null) return null;

	// 3️⃣ Remove trailing .0
	return Number(rating || 0).toFixed(1);
};

/* ---------------- HELPERS ---------------- */

const DEFAULT_CENTER = { lat: 18.4861, lng: -69.9312, zoom: 13 };
const MOVE_THRESHOLD_KM = 8; // city-level movement
const MAPBOX_TOKEN = process.env.REACT_APP_MAP_BOX_TOKEN;
const DEFAULT_MARKER_ICON = new L.Icon.Default();

const ShopMapPopup = ({ shop }) => {
	return (
		<div className="sf-popup-card">
			<Link to={`/shops/${shop.shopId}`}>
				{/* Image carousel */}
				<div className="sf-popup-images">
					{shop.coverImage ? <img src={shop.coverImage} alt={shop.name} /> : <div className="sf-popup-image-placeholder" />}
				</div>

				<div className="sf-popup-body">
					<div className="sf-popup-body-inner">
						<h4 className="sf-popup-title">{shop.name}</h4>

						{shop.rating && (
							<p className="sf-popup-rating">
								★ {formatRating(shop.rating)} {shop.reviewCount ? `(${shop.reviewCount})` : ""}
							</p>
						)}
					</div>

					{shop.city && (
						<p className="sf-popup-address">
							{shop.city}, {shop.country}
						</p>
					)}

					{/* {shop.city && (
						<p className="sf-popup-address">
							{shop.address}
						</p>
					)} */}
				</div>
			</Link>
		</div>
	);
};

const kmBetween = (a, b) => {
	const R = 6371;
	const dLat = ((b.lat - a.lat) * Math.PI) / 180;
	const dLng = ((b.lng - a.lng) * Math.PI) / 180;

	const lat1 = (a.lat * Math.PI) / 180;
	const lat2 = (b.lat * Math.PI) / 180;

	const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

	return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

/* ---------------- COMPONENT ---------------- */

const DiscoverMapPage = () => {
	const [shops, setShops] = useState([]);
	const [selectedShop, setSelectedShop] = useState(null);
	const [selectedShopId, setSelectedShopId] = useState(null);
	const [loading, setLoading] = useState(true);
	const [lastFetchCenter, setLastFetchCenter] = useState(null);

	const mapRef = useRef(null);

	const navigate = useNavigate();

	/* Initial load — stable endpoint */
	useEffect(() => {
		const load = async () => {
			setLoading(true);
			const res = await generalRequest.getPublicShopsForMap();
			setShops(res.shops || []);
			setLoading(false);
		};
		load();
	}, []);

	/* Ensure Leaflet renders correctly (once data exists) */
	useEffect(() => {
		if (!mapRef.current || shops.length === 0) return;

		requestAnimationFrame(() => {
			mapRef.current.invalidateSize();
			mapRef.current.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_CENTER.zoom);
		});
	}, [shops]);

	/* Map movement hook — refetch ONLY on large moves */
	const handleBoundsChange = async ({ ne, sw, center }) => {
		setSelectedShop(null);

		// First movement → fetch once
		if (!lastFetchCenter) {
			setLastFetchCenter(center);

			const res = await generalRequest.getPublicShopsForMap({
				neLat: ne.lat,
				neLng: ne.lng,
				swLat: sw.lat,
				swLng: sw.lng,
			});

			if (res.shops?.length) {
				setShops(res.shops);
			}
			return;
		}

		const movedKm = kmBetween(lastFetchCenter, center);

		// Ignore zoom / tiny pans
		if (movedKm < MOVE_THRESHOLD_KM) return;

		// Significant move → refetch
		setLastFetchCenter(center);

		const res = await generalRequest.getPublicShopsForMap({
			neLat: ne.lat,
			neLng: ne.lng,
			swLat: sw.lat,
			swLng: sw.lng,
		});
		setShops(res.shops);

		// if (res.shops?.length) {
		// 	setShops(res.shops);
		// }
	};

	const ratingIconCache = new Map();

	const markerIconCache = new Map();

	const getMarkerIcon = (rating) => {
		const displayRating = rating ? formatRating(rating) : "";

		const cacheKey = displayRating || "no-rating";

		if (markerIconCache.has(cacheKey)) {
			return markerIconCache.get(cacheKey);
		}

		const icon = L.divIcon({
			className: "sf-marker",
			html: `
			<div class="sf-marker-inner ${displayRating ? "" : "no-rating"}">
				<svg width="36" height="48" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M18 0C8.1 0 0 8.1 0 18c0 13.5 18 30 18 30s18-16.5 18-30C36 8.1 27.9 0 18 0z"
						fill="#111"
					/>
					${displayRating ? `<text x="18" y="24" text-anchor="middle" fill="#fff" font-size="11" font-weight="600">${displayRating}</text>` : ""}
				</svg>
			</div>
		`,
			iconSize: [36, 48],
			iconAnchor: [18, 48],
			popupAnchor: [0, -36],
		});

		markerIconCache.set(cacheKey, icon);
		return icon;
	};

	const MapServicesList = ({ shop }) => {
		if (!Array.isArray(shop.services) || shop.services.length === 0) return null;
	
		const visibleServices = shop.services.slice(0, 3);
	
		return (
			<div className="sf-map-services">
				{visibleServices.map((service) => (
					<div key={service.serviceId} className="sf-map-service-row">
						<div className="sf-map-service-info">
							<span className="sf-map-service-name">
								{service.name}
							</span>
	
							<span className="sf-map-service-duration">
								{service.duration} min
							</span>
						</div>
	
						<div className="sf-map-service-right">
							<span className="sf-map-service-price">
								{service.price} {service.currency}
							</span>
						</div>
					</div>
				))}
	
				{/* Optional indicator */}
				{shop.services.length > 3 && (
					<div className="sf-map-services-more">
						+ {shop.services.length - 3} more services
					</div>
				)}
			</div>
		);
	};
	
	

	return (
		<div className="discover-map-layout">
			{/* LEFT / SIDE LIST */}
			<div className="discover-map-list">
				<h3 className="discover-map-list-title">Shops ({shops.length})</h3>

				<div className="discover-map-list-items">
					{loading ? (
						<DiscoverMapListSkeleton count={6} />
					) : (
						shops.map((shop) => (
							<div key={shop.shopId} className="discover-map-list-item" onClick={() => navigate(`/shops/${shop.shopId}`)}>
								<div className="discover-map-list-image">
									{shop.coverImage ? <img src={shop.coverImage} alt={shop.name} /> : <div className="image-placeholder" />}
								</div>

								<div className="discover-map-list-info">
									<h4>{shop.name}</h4>

									{shop.rating && (
										<p className="rating">
											★ {formatRating(shop.rating)}
											{shop.reviewCount ? ` (${shop.reviewCount})` : ""}
										</p>
									)}
								</div>

								<div className="discover-map-list-info">{shop.address && <p className="location">{shop.address}</p>}</div>
								<MapServicesList shop={shop} />
							</div>
						))
					)}
				</div>
			</div>

			{/* MAP */}
			<div className="discover-map-container">
				<MapContainer
					whenCreated={(map) => (mapRef.current = map)}
					center={[DEFAULT_CENTER.lat, DEFAULT_CENTER.lng]}
					zoom={DEFAULT_CENTER.zoom}
					className="discover-map"
					scrollWheelZoom
					attributionControl={false}
					zoomControl={false}
				>
					{MAPBOX_TOKEN ? (
						// <TileLayer
						// 	url={`https://api.maptiler.com/maps/streets/{z}/{x}/{y}@2x.png?key=${MAPBOX_TOKEN}`}
						// 	tileSize={512}
						// 	zoomOffset={-1}
						// 	attribution="© MapTiler © OpenStreetMap contributors"
						// />
						<TileLayer
							url={`https://api.mapbox.com/styles/v1/iwhite/cmjwicu52001001qo18qxh9i4/tiles/512/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
							tileSize={512}
							zoomOffset={-1}
							attribution="© Mapbox © OpenStreetMap"
						/>
					) : (
						<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
					)}

					<MapEvents onBoundsChange={handleBoundsChange} />

					{shops.map((shop) =>
						typeof shop.lat === "number" && typeof shop.lng === "number" ? (
							<Marker key={shop.shopId} position={[shop.lat, shop.lng]} icon={getMarkerIcon(shop.rating)}>
								<Popup closeButton={false} autoPan offset={[0, -10]} className="sf-map-popup" autoClose={true} closeOnClick={false}>
									<ShopMapPopup shop={shop} />
								</Popup>
							</Marker>
						) : null
					)}
				</MapContainer>
			</div>
		</div>
	);
};

export default DiscoverMapPage;
